/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import StructureContainers from '@Services/StructureContainers';
import { StructureOptionsBadges } from '@Containers/StructureOptions';
import RxThumbnail from '@Services/RxThumbnail';
import { useDispatch } from 'react-redux';
import { ButtonWithIcon } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';
import MarvinHelpers from '@Services/MarvinHelpers';
import get from 'lodash/get';
import { updateStructureandAdvancedAction } from '@Pages/QueryBuilder/QueryletActions';
import history from '@AppRoutes/history';
import { useLocation } from 'react-router-dom';
import isEqual from 'lodash/isEqual';

function Structure({ structureType }) {
  const [structure, setStructure] = useState(structureType);
  const dispatch = useDispatch();
  const location = useLocation();
  const importedQueryValue = location.state?.importedQuery;
  // Track if we've already dispatched once for options update
  const hasDispatchedInitialOptionsRef = useRef(false);

  function getStructurePreviewfromData() {
    const { structureData } = structure;

    RxThumbnail.gitStructureQueryLetThumbnail(structureData)
      .then((data) => {
        if (!data) {
          Logger.error('Cannot retrieve structure preview image');
        }
        setStructure((_structure) => ({
          ..._structure,
          structurePreview: data?.url || null,
          previewSize: MarvinHelpers.getParams(MarvinHelpers.getBoundCount(structureData, 4)),
        }));
      })
      .catch(Logger.reject);
  }

  function setStructureFn(structureContainer) {
    setStructure((_structure) => {
      const updatedStructure = {
        ..._structure,
        ...(structureContainer.data && {
          structureData: structureContainer.data,
          previewSize: MarvinHelpers.getParams(MarvinHelpers.getBoundCount(structureContainer.data, 4)),
        }),
        ...(structureContainer.preview && { structurePreview: structureContainer.preview }),
        ...(structureContainer.name && { structureName: structureContainer.name }),
        ...(structureContainer.options && { options: structureContainer.options }),
      };
      if (!structureType.structureData) {
        dispatch(updateStructureandAdvancedAction(updatedStructure));
      }

      return updatedStructure;
    });
    if (!structureContainer.preview) {
      getStructurePreviewfromData();
    }
  }

  useEffect(() => {
    StructureContainers.getContainerByUID(structure.structureUID)

      .then((structureContainer) => {
        if (isEmpty(structureContainer)) {
          if (structure.structureData) {
            getStructurePreviewfromData();
          }
        } else {
          setStructureFn(structureContainer);
        }
      })
      .catch(Logger.reject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onStructureBuilderClick() {
    StructureContainers.saveContainer(structure.structureUID, {
      data: structure.structureData,
      options: structure.options,
      preview: structure.structurePreview,
      name: structure.structureName,
    })
      .then(() =>
        history.push({
          pathname: `/structure-editor/${structure.structureUID}`,
          state: { target: 'advancedSearch', structureUID: structure.structureUID },
        })
      )
      .catch(Logger.error);
  }

  function clearStructure($event) {
    if ($event) {
      $event.stopPropagation();
      $event.preventDefault();
    }

    StructureContainers.removeContainerByUID(structure.structureUID);
    setStructure((prevStructure) => {
      const updatedStructure = {
        ...prevStructure,
        structurePreview: null,
        structureData: null,
        options: null,
        structureName: null,
      };
      dispatch(updateStructureandAdvancedAction(updatedStructure));

      return updatedStructure;
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById(`draw-structure-${structure.structureUID}`)?.focus();
      });
    });
  }

  const updateOptions = () => {
    StructureContainers.getContainerByUID(structure.structureUID)
      .then((structureContainer) => {
        const structureContainerOptions = get(structureContainer, 'options');
        setStructure((_structure) => {
          const updatedStructure = {
            ..._structure,
            ...(structureContainerOptions && {
              options: structureContainerOptions,
            }),
          };
          // First time: dispatch unconditionally; afterwards only when deep-changed otherwise it is not working for replace form
          if (!hasDispatchedInitialOptionsRef.current || !isEqual(_structure.options, structureContainerOptions)) {
            dispatch(updateStructureandAdvancedAction(updatedStructure));
            hasDispatchedInitialOptionsRef.current = true;
          }
          return updatedStructure;
        });
      })
      .catch(Logger.reject);
  };

  const handleStructureOptionClick = () => {
    updateOptions();
    onStructureBuilderClick();
  };

  return (
    (!structure.exists || !structure.canExist) &&
    (!structure.structurePreview ? (
      <LinkElement
        href="#"
        iconSprite="DrawStructureNew"
        id={`draw-structure-${structure.structureUID}`}
        onClick={(e) => {
          e.preventDefault();
          onStructureBuilderClick();
        }}
        data-e2e="e2e-build-query-structuredrawing-draw"
        className="build-query-structure-drawing__draw-button build-query-structure-drawing__querybuilder-draw"
        aria-label="Draw Structure">
        Draw Structure / Reaction
      </LinkElement>
    ) : (
      <div
        className="build-query-structure-drawing__structure build-query-structure-drawing__qb-structure"
        onClick={() => {
          onStructureBuilderClick();
        }}
        aria-label="drawn chemical structure, press enter to edit structure"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onStructureBuilderClick();
          }
        }}>
        <ButtonWithIcon
          data-e2e="querybuiler-clearStructure-button"
          sprite="Close"
          linkIconSize="xs"
          iconOnly
          id={`clear-structure-${structure.structureUID}`}
          type="link"
          onClick={(e) => clearStructure(e)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              clearStructure(e);
            }
            e.stopPropagation();
          }}
          className="build-query-structure-drawing__structure_clearButton"
          aria-label="Clear quick search structure">
          Remove Structure
        </ButtonWithIcon>
        <img
          className="build-query-structure-drawing__structure_image"
          src={structure.structurePreview}
          alt="drawn chemical structure"
          data-e2e="e2e-build-query-structure-drawing-image"
        />
        <div className="build-query-structure-drawing__structure_editButton e2e-build-query-structure-drawing-edit">
          <StructureOptionsBadges
            id={structure.structureUID}
            structureOptions={importedQueryValue || structure.options ? structure.options : null}
            updateStructureOptionsCallback={updateOptions}
            source="querybuilder"
            onStructureOptionClick={handleStructureOptionClick}
          />
        </div>
      </div>
    ))
  );
}

Structure.propTypes = {
  structureType: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    displayName: PropTypes.string,
    defaultOperation: PropTypes.string,
    canExist: PropTypes.bool,
    dataType: PropTypes.string,
    databases: PropTypes.arrayOf(PropTypes.string),
    databaseId: PropTypes.string,
    childrenCount: PropTypes.number,

    structureData: PropTypes.any,
    structureUID: PropTypes.string.isRequired,
    children: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired,
        defaultBoundOperator: PropTypes.string.isRequired,
        canExist: PropTypes.bool.isRequired,
        dataType: PropTypes.string.isRequired,
        databases: PropTypes.arrayOf(PropTypes.string).isRequired,
        databaseId: PropTypes.string.isRequired,
        childrenCount: PropTypes.number.isRequired,
        minimized: PropTypes.bool.isRequired,
        value: PropTypes.string.isRequired,
        operation: PropTypes.string.isRequired,
      })
    ),
    minimized: PropTypes.bool,
    operator: PropTypes.string,
    exist: PropTypes.bool,
    uuid: PropTypes.string,
  }).isRequired,
};

export default Structure;
